import React from "react";
import { Link } from "react-router-dom";
import { 
  Github, 
  Linkedin, 
  Twitter, 
  BookOpen,
  ArrowUpRight
} from "lucide-react";
import { Container } from "./layout/container";
import { trackSocialClick, trackExternalLink, trackDownload } from '../utils/analytics';
import { useLanguage } from '../context/LanguageContext';
import { getResumePath } from '../data/resume';

export default function Footer() {
  const { language, strings } = useLanguage();
  const currentYear = new Date().getFullYear();

  const navigation = {
    main: [
      { name: strings.footer.home, href: "/" },
      { name: strings.footer.selectedWork, href: "/#projects" },
      { name: strings.footer.buildLab, href: "/#build-lab" },
      { name: strings.footer.experience, href: "/experience" },
      { name: strings.footer.contact, href: "/contact-me" },
    ],
    resources: [
      { name: strings.footer.resume, href: getResumePath(language), external: true },
      { name: strings.footer.medium, href: "https://nandanpriyadarshi.medium.com/", external: true },
    ],
    legal: [
      { name: strings.footer.privacyPolicy, href: "/privacy-policy" },
      { name: strings.footer.termsCondition, href: "/term-condition" },
    ],
    social: [
      { name: "GitHub", href: "https://github.com/nandanosql", icon: Github },
      { name: "LinkedIn", href: "https://linkedin.com/in/nandan-priyadarshi", icon: Linkedin },
      { name: "X", href: "https://x.com/nandanpri", icon: Twitter },
      { name: "Medium", href: "https://nandanpriyadarshi.medium.com/", icon: BookOpen },
    ],
  };

  return (
    <footer className="relative border-t border-white/5 bg-[#0B0B0F]">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent pointer-events-none" />
      
      <Container className="relative">
        {/* Main Footer Content */}
        <div className="py-12 lg:py-16">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:gap-12">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="inline-flex items-center gap-2 group">
                <div className="h-8 w-8 rounded-full bg-gradient-to-tr from-slate-300 to-slate-500 shadow-lg shadow-black/30 transition-transform group-hover:scale-105" />
                <span className="text-sm font-semibold tracking-[0.2em] uppercase text-white/80 group-hover:text-white transition-colors">
                  {strings.nav.brand}
                </span>
              </Link>
              <p className="mt-4 text-sm text-white/50 max-w-xs leading-relaxed">
                {strings.footer.tagline}
              </p>
              
              {/* Social Links */}
              <div className="mt-6 flex gap-3">
                {navigation.social.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/50 transition-[transform,background-color,border-color,color] duration-150 hover:border-white/20 hover:bg-white/10 hover:text-white active:scale-[0.97]"
                    aria-label={item.name}
                    onClick={() => trackSocialClick(item.name, 'Footer')}
                  >
                    <item.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>

            {/* Navigation Column */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70 mb-4">
                {strings.footer.navigate}
              </h3>
              <ul className="space-y-3">
                {navigation.main.map((item) => (
                  <li key={item.name}>
                    {item.href.startsWith("/#") ? (
                      <a
                        href={item.href}
                        className="text-sm text-white/50 hover:text-white transition-colors inline-flex items-center gap-1 group"
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        className="text-sm text-white/50 hover:text-white transition-colors inline-flex items-center gap-1 group"
                      >
                        {item.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70 mb-4">
                {strings.footer.resources}
              </h3>
              <ul className="space-y-3">
                {navigation.resources.map((item) => (
                  <li key={item.name}>
                    {item.external ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-white/50 hover:text-white transition-colors inline-flex items-center gap-1 group"
                        onClick={() => {
                          if (item.name === strings.footer.resume || item.name === 'Resume') {
                            trackDownload('Resume PDF');
                          } else {
                            trackExternalLink(item.href, item.name);
                          }
                        }}
                      >
                        {item.name}
                        <ArrowUpRight className="h-3 w-3 opacity-0 -translate-y-0.5 translate-x-0.5 group-hover:opacity-100 transition-[transform,opacity] duration-150" />
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        className="text-sm text-white/50 hover:text-white transition-colors"
                      >
                        {item.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Column */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70 mb-4">
                {strings.footer.legal}
              </h3>
              <ul className="space-y-3">
                {navigation.legal.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-sm text-white/50 hover:text-white transition-colors"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/40">
              © {currentYear} Nandan Priyadarshi. {strings.footer.rightsReserved}
            </p>
            <p className="text-xs text-white/40">
              {strings.footer.bottomTagline}
            </p>
          </div>
        </div>
      </Container>
    </footer>
  );
}
